import axios from 'axios';
import toast from 'react-hot-toast';
import { TENANT_ORG_ID_STORAGE_KEY } from '../constants/tenant';
import i18n from '../i18n';
import { userFacingError } from '../i18n/userFacingError';

import { resolveApiBaseUrl, apiPath } from '../utils/apiBaseUrl';
import { getInstallId } from '../push/installId';

// Em producao usa paths relativos; em dev localhost usa REACT_APP_API_URL.
export const API_BASE_URL = resolveApiBaseUrl();
export { apiPath };

/** Headers para `fetch` (ex.: SSE) — alinhado ao interceptor do axios (token + tenant). */
export function getApiAuthHeaders(extra = {}) {
  const headers = { Accept: 'text/event-stream', ...extra };
  const token = localStorage.getItem('authToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  try {
    const orgId = localStorage.getItem(TENANT_ORG_ID_STORAGE_KEY);
    if (orgId) {
      headers['X-Tenant-Org-Id'] = orgId;
    }
    headers['X-Install-Id'] = getInstallId();
  } catch {
    /* ignore */
  }
  return headers;
}

// Instância do axios com configurações padrão
const api = axios.create({
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// baseURL resolvido a cada request (subdominio do tenant muda a origem)
api.interceptors.request.use(
  (config) => {
    const base = resolveApiBaseUrl();
    if (base) {
      config.baseURL = base;
    } else if (config.url && !config.url.startsWith('http')) {
      config.baseURL = undefined;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    try {
      config.headers['X-Install-Id'] = getInstallId();
    } catch {
      /* ignore */
    }
    const path = typeof config.url === 'string' ? config.url : '';
    const skipTenant =
      path.includes('/api/v1/organizations/resolve') ||
      path.includes('/api/v1/organizations/register-with-admin') ||
      path.includes('/api/v1/organizations/by-slug/') ||
      path.includes('/api/v1/organizations/platform/') ||
      path.includes('organizations/resolve') ||
      path.includes('/api/v1/pricing/') ||
      path.includes('/api/v1/subscriptions/checkout-url') ||
      path.includes('/api/v1/auth/password-reset/confirm') ||
      path.includes('/api/v1/admin/notifications/') ||
      (String(config.method || '').toLowerCase() === 'post' &&
        (path === '/api/v1/subscriptions' || path.endsWith('/api/v1/subscriptions')));
    if (!skipTenant) {
      try {
        const orgId = localStorage.getItem(TENANT_ORG_ID_STORAGE_KEY);
        if (orgId) {
          config.headers['X-Tenant-Org-Id'] = orgId;
        }
      } catch {
        /* ignore */
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let refreshInFlight = null;

function kickToLogin() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/login')) {
    window.location.href = '/login';
  }
}

async function refreshAccessToken() {
  const refresh_token = localStorage.getItem('refreshToken');
  if (!refresh_token) throw new Error('No refresh token');
  const res = await api.post('/api/v1/auth/refresh', { refresh_token });
  const access = res.data?.access_token;
  if (!access) throw new Error('No access token');
  localStorage.setItem('authToken', access);
  if (res.data.refresh_token) localStorage.setItem('refreshToken', res.data.refresh_token);
  return access;
}

// Interceptor para tratamento de respostas
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const url = String(original.url || '');
    const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/password-reset');
    if (error.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        if (!refreshInFlight) {
          refreshInFlight = refreshAccessToken().finally(() => {
            refreshInFlight = null;
          });
        }
        const token = await refreshInFlight;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        if (
          !url.includes('/processos/consulta') &&
          !url.includes('/certidoes-viabilidade/consulta') &&
          !url.includes('/complaint/public') &&
          !url.includes('/taxas/webhooks')
        ) {
          kickToLogin();
        }
      }
    }
    if (error.response?.status === 403) {
      const method = String(original.method || 'get').toLowerCase();
      if (method !== 'get' && method !== 'head') {
        toast.error(userFacingError(error, 'forbiddenError'));
      }
    }
    if (error.response?.status === 402) {
      toast.error(userFacingError(error, 'billingBlocked'));
    }
    return Promise.reject(error);
  }
);

// Serviços para Identity Service
export const identityService = {
  // Pessoas
  getPersons: (params = {}) => api.get('/api/v1/identity/persons', { params }),
  getPerson: (id) => api.get(`/api/v1/identity/persons/${id}`),
  createPerson: (data) => api.post('/api/v1/identity/persons', data),
  /** Cria pessoa + usuário auth + convite SMTP (somente novo cadastro no tenant atual). */
  createPersonWithAccessInvite: (data) =>
    api.post('/api/v1/identity/persons/with-access-invite', data),
  signupRequerente: (data) => api.post('/api/v1/identity/public/requerente-signup', data),
  updatePerson: (id, data) => api.put(`/api/v1/identity/persons/${id}`, data),
  deletePerson: (id) => api.delete(`/api/v1/identity/persons/${id}`),
  getPersonByCpf: (cpf) =>
    api.get('/api/v1/identity/persons/by-cpf', { params: { cpf } }),
  
  // Empresas (cadastro jurídico — rota sob /api/v1/identity no gateway)
  getCompanies: (params = {}) => api.get('/api/v1/identity/companies/', { params }),
  getCompany: (id) => api.get(`/api/v1/identity/companies/${id}`),
  createCompany: (data) => api.post('/api/v1/identity/companies/', data),
  /** Cria empresa + usuário auth + convite SMTP. */
  createCompanyWithAccessInvite: (data) =>
    api.post('/api/v1/identity/companies/with-access-invite', data),
  updateCompany: (id, data) => api.put(`/api/v1/identity/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/api/v1/identity/companies/${id}`),
  getCompanyByCnpj: (cnpj) =>
    api.get('/api/v1/identity/companies/by-cnpj', { params: { cnpj } }),
  
  // Clientes
  getClients: (params = {}) => api.get('/api/v1/identity/clients/', { params }),
  getClient: (id) => api.get(`/api/v1/identity/clients/${id}`),
  createClient: (data) => api.post('/api/v1/identity/clients/', data),
  updateClient: (id, data) => api.put(`/api/v1/identity/clients/${id}`, data),
  deleteClient: (id) => api.delete(`/api/v1/identity/clients/${id}`),
  
  // Empresas (Enterprises) — trailing slash evita 307 do FastAPI (Location http:// → Network Error)
  getEnterprises: (params = {}) => api.get('/api/v1/identity/enterprises/', { params }),
  getEnterprise: (id) => api.get(`/api/v1/identity/enterprises/${id}`),
  createEnterprise: (data) => api.post('/api/v1/identity/enterprises/', data),
  createEnterpriseWithAccessInvite: (data) =>
    api.post('/api/v1/identity/enterprises/with-access-invite', data),
  updateEnterprise: (id, data) => api.put(`/api/v1/identity/enterprises/${id}`, data),
  deleteEnterprise: (id) => api.delete(`/api/v1/identity/enterprises/${id}`),

  /** useCrud — empreendimentos (lista normalizada) */
  enterpriseCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/identity/enterprises/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/identity/enterprises/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/identity/enterprises/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/identity/enterprises/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/identity/enterprises/${id}`),
  },

  // Métodos genéricos para useCrud
  getList: (params = {}) => api.get('/api/v1/identity/clients/', { params }),
  getItem: (id) => api.get(`/api/v1/identity/clients/${id}/`),
  create: (data) => api.post('/api/v1/identity/clients/', data),
  update: (id, data) => api.put(`/api/v1/identity/clients/${id}/`, data),
  delete: (id) => api.delete(`/api/v1/identity/clients/${id}/`),

  /** Fotos do empreendimento (gateway → identity-service) */
  listEnterpriseFotos: (enterpriseId) =>
    api.get(`/api/v1/identity/enterprise-fotos/by-enterprise/${enterpriseId}`),
  uploadEnterpriseFoto: (enterpriseId, file, caption) => {
    const fd = new FormData();
    fd.append('file', file);
    if (caption != null && caption !== '') fd.append('caption', caption);
    return api.post(`/api/v1/identity/enterprise-fotos/upload/${enterpriseId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteEnterpriseFoto: (fotoId) =>
    api.delete(`/api/v1/identity/enterprise-fotos/${fotoId}`),
  getEnterpriseFotoFileBlob: (fotoId) =>
    api.get(`/api/v1/identity/enterprise-fotos/${fotoId}/file`, { responseType: 'blob' }),

  listAreasProtegidas: () => api.get('/api/v1/identity/areas-protegidas/'),
  createAreaProtegida: (data) => api.post('/api/v1/identity/areas-protegidas/', data),
  deleteAreaProtegida: (id) => api.delete(`/api/v1/identity/areas-protegidas/${id}`),
  intersectEnterprise: (enterpriseId) =>
    api.get('/api/v1/identity/geo/intersect', { params: { enterprise_id: enterpriseId } }),
};

// Serviços para Contact Service
export const contactService = {
  // Contatos (via API Gateway prefix /api/v1/contact)
  getContacts: (params = {}) => api.get('/api/v1/contact/contacts/', { params }),
  getContact: (id) => api.get(`/api/v1/contact/contacts/${id}`),
  createContact: (data) => api.post('/api/v1/contact/contacts/', data),
  updateContact: (id, data) => api.put(`/api/v1/contact/contacts/${id}`),
  deleteContact: (id) => api.delete(`/api/v1/contact/contacts/${id}`),
  getContactsByPerson: (personId) => api.get(`/api/v1/contact/contacts/person/${personId}`),
  searchContacts: (query) => api.get(`/api/v1/contact/contacts/search?q=${encodeURIComponent(query)}`),
  
  // Posições (gateway: /api/v1/contact/positions/)
  getPositions: (params = {}) => api.get('/api/v1/contact/positions/', { params }),
  getPosition: (id) => api.get(`/api/v1/contact/positions/${id}`),
  createPosition: (data) => api.post('/api/v1/contact/positions/', data),
  updatePosition: (id, data) => api.put(`/api/v1/contact/positions/${id}`, data),
  deletePosition: (id) => api.delete(`/api/v1/contact/positions/${id}`),
  getPositionsByLevel: (level) => api.get(`/api/v1/contact/positions/level/${level}`),

  positionCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/contact/positions/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/contact/positions/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/contact/positions/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/contact/positions/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/contact/positions/${id}`),
  },
  contactCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/contact/contacts/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/contact/contacts/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/contact/contacts/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/contact/contacts/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/contact/contacts/${id}`),
  },
};

// Serviços para Catalog Service
export const catalogService = {
  // Tamanhos de Empresa
  getCompanySizes: (params = {}) => api.get('/api/v1/catalog/company-sizes/', { params }),
  getCompanySize: (id) => api.get(`/api/v1/catalog/company-sizes/${id}`),
  createCompanySize: (data) => api.post('/api/v1/catalog/company-sizes/', data),
  updateCompanySize: (id, data) => api.put(`/api/v1/catalog/company-sizes/${id}`, data),
  deleteCompanySize: (id) => api.delete(`/api/v1/catalog/company-sizes/${id}`),
  
  // Status de Empresa
  getCompanyStatuses: (params = {}) => api.get('/api/v1/catalog/company-statuses/', { params }),
  getCompanyStatus: (id) => api.get(`/api/v1/catalog/company-statuses/${id}`),
  createCompanyStatus: (data) => api.post('/api/v1/catalog/company-statuses/', data),
  updateCompanyStatus: (id, data) => api.put(`/api/v1/catalog/company-statuses/${id}`, data),
  deleteCompanyStatus: (id) => api.delete(`/api/v1/catalog/company-statuses/${id}`),
  
  // Atividades Econômicas
  getEconomicActivities: (params = {}) => api.get('/api/v1/catalog/economic-activities/', { params }),
  getEconomicActivity: (id) => api.get(`/api/v1/catalog/economic-activities/${id}`),
  createEconomicActivity: (data) => api.post('/api/v1/catalog/economic-activities/', data),
  updateEconomicActivity: (id, data) => api.put(`/api/v1/catalog/economic-activities/${id}`, data),
  deleteEconomicActivity: (id) => api.delete(`/api/v1/catalog/economic-activities/${id}`),
  searchEconomicActivities: (query) =>
    api.get('/api/v1/catalog/economic-activities/', { params: { q: query } }),

  /** useCrud — resposta normalizada para lista (array ou { items|data }) */
  companySizeCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/catalog/company-sizes/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/catalog/company-sizes/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/catalog/company-sizes/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/catalog/company-sizes/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/catalog/company-sizes/${id}`),
  },
  companyStatusCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/catalog/company-statuses/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/catalog/company-statuses/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/catalog/company-statuses/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/catalog/company-statuses/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/catalog/company-statuses/${id}`),
  },
  economicActivityCrud: {
    getList: (params = {}) =>
      api.get('/api/v1/catalog/economic-activities/', { params }).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items ?? d?.data ?? [];
      }),
    getItem: (id) => api.get(`/api/v1/catalog/economic-activities/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/catalog/economic-activities/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/catalog/economic-activities/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/catalog/economic-activities/${id}`),
  },
};

// Serviços para Professional Service
export const professionalService = {
  // Responsáveis Técnicos
  getTechnicalResponsibles: (params = {}) => api.get('/api/v1/professional/technical-responsibles/', { params }),
  getTechnicalResponsible: (id) => api.get(`/api/v1/professional/technical-responsibles/${id}`),
  createTechnicalResponsible: (data) => api.post('/api/v1/professional/technical-responsibles/', data),
  updateTechnicalResponsible: (id, data) => api.put(`/api/v1/professional/technical-responsibles/${id}`, data),
  deleteTechnicalResponsible: (id) => api.delete(`/api/v1/professional/technical-responsibles/${id}`),
  getTechnicalResponsiblesByPerson: (personId) => api.get(`/api/v1/professional/technical-responsibles/person/${personId}`),
  
  // Conselhos Profissionais
  getProfessionalCouncils: (params = {}) => api.get('/api/v1/professional/professional-councils/', { params }),
  getProfessionalCouncil: (id) => api.get(`/api/v1/professional/professional-councils/${id}`),
  createProfessionalCouncil: (data) => api.post('/api/v1/professional/professional-councils/', data),
  updateProfessionalCouncil: (id, data) => api.put(`/api/v1/professional/professional-councils/${id}`, data),
  deleteProfessionalCouncil: (id) => api.delete(`/api/v1/professional/professional-councils/${id}`),
  getProfessionalCouncilsByRegion: (region) => api.get(`/api/v1/professional/professional-councils/region/${region}`),
  
  // Formações Profissionais
  getProfessionalFormations: (params = {}) => api.get('/api/v1/professional/professional-formations/', { params }),
  getProfessionalFormation: (id) => api.get(`/api/v1/professional/professional-formations/${id}`),
  createProfessionalFormation: (data) => api.post('/api/v1/professional/professional-formations/', data),
  updateProfessionalFormation: (id, data) => api.put(`/api/v1/professional/professional-formations/${id}`, data),
  deleteProfessionalFormation: (id) => api.delete(`/api/v1/professional/professional-formations/${id}`),
  getProfessionalFormationsByPerson: (personId) => api.get(`/api/v1/professional/professional-formations/person/${personId}`),
  getCompletedProfessionalFormations: (personId) => api.get(`/api/v1/professional/professional-formations/completed/${personId}`),

  getTechnicalResponsibleAssignments: (params = {}) =>
    api.get('/api/v1/professional/technical-responsible-assignments/', { params }),
  createTechnicalResponsibleAssignment: (data) =>
    api.post('/api/v1/professional/technical-responsible-assignments/', data),
  deleteTechnicalResponsibleAssignment: (id) =>
    api.delete(`/api/v1/professional/technical-responsible-assignments/${id}`),

  technicalResponsibleCrud: {
    getList: (params = {}) => api.get('/api/v1/professional/technical-responsibles/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/professional/technical-responsibles/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/professional/technical-responsibles/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/professional/technical-responsibles/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/professional/technical-responsibles/${id}`).then((r) => r.data),
  },
  professionalCouncilCrud: {
    getList: (params = {}) => api.get('/api/v1/professional/professional-councils/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/professional/professional-councils/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/professional/professional-councils/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/professional/professional-councils/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/professional/professional-councils/${id}`).then((r) => r.data),
  },
  professionalFormationCrud: {
    getList: (params = {}) => api.get('/api/v1/professional/professional-formations/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/professional/professional-formations/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/professional/professional-formations/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/professional/professional-formations/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/professional/professional-formations/${id}`).then((r) => r.data),
  },
};

export const routingService = {
  orderTrip: (stops, extras = {}) => api.post('/api/v1/routing/trip', { stops, ...extras }),
};

// Serviços para Complaint Service (Denúncias)
export const complaintService = {
  // Categorias
  getCategories: (params = {}) => api.get('/api/v1/complaint/categories/', { params }),
  getCategory: (id) => api.get(`/api/v1/complaint/categories/${id}`),
  createCategory: (data) => api.post('/api/v1/complaint/categories/', data),
  updateCategory: (id, data) => api.put(`/api/v1/complaint/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/api/v1/complaint/categories/${id}`),
  getCategoriesByParent: (parentId) => api.get('/api/v1/complaint/categories/by-parent', { params: parentId != null ? { parent_id: parentId } : {} }),

  // Denúncias
  getComplaints: (params = {}) => api.get('/api/v1/complaint/complaints/', { params }),
  getComplaint: (id) => api.get(`/api/v1/complaint/complaints/${id}`),
  createComplaint: (data) => api.post('/api/v1/complaint/complaints/', data),
  updateComplaint: (id, data) => api.put(`/api/v1/complaint/complaints/${id}`, data),
  deleteComplaint: (id) => api.delete(`/api/v1/complaint/complaints/${id}`),
  getComplaintsByStatus: (status, params = {}) => api.get(`/api/v1/complaint/complaints/status/${status}`, { params }),
  getComplaintsByCategory: (categoryId, params = {}) => api.get(`/api/v1/complaint/complaints/category/${categoryId}`, { params }),
  getComplaintsByClient: (clientId) => api.get(`/api/v1/complaint/complaints/client/${clientId}`),
  getComplaintsByEnterprise: (enterpriseId) =>
    api.get(`/api/v1/complaint/complaints/enterprise/${enterpriseId}`),
  getComplaintsByLicense: (licenseId) => api.get(`/api/v1/complaint/complaints/license/${licenseId}`),
  getPublicCategories: () => api.get('/api/v1/complaint/public/categories'),
  createPublicDenuncia: (data) => api.post('/api/v1/complaint/public/denuncias', data),
  consultarDenunciaPublica: (numero) =>
    api.get('/api/v1/complaint/public/denuncias/consulta', { params: { numero } }),
  listComplaintFotos: (complaintId) =>
    api.get(`/api/v1/complaint/complaint-fotos/by-complaint/${complaintId}`),
  uploadComplaintFoto: (complaintId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/v1/complaint/complaint-fotos/upload/${complaintId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPublicDenunciaFoto: (protocolo, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/v1/complaint/public/denuncias/${encodeURIComponent(protocolo)}/fotos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteComplaintFoto: (fotoId) => api.delete(`/api/v1/complaint/complaint-fotos/${fotoId}`),
  getComplaintFotoFileBlob: (fotoId) =>
    api.get(`/api/v1/complaint/complaint-fotos/${fotoId}/file`, { responseType: 'blob' }),

  // Adapters para useCrud - Categorias
  categoryCrud: {
    getList: (params = {}) => api.get('/api/v1/complaint/categories/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/complaint/categories/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/complaint/categories/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/complaint/categories/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/complaint/categories/${id}`),
  },
  // Adapters para useCrud - Denúncias
  complaintCrud: {
    getList: (params = {}) => api.get('/api/v1/complaint/complaints/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/complaint/complaints/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/complaint/complaints/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/complaint/complaints/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/complaint/complaints/${id}`),
  },
};

// Serviços para Institution Service (Órgãos, Setores, Cargos, Servidores)
export const institutionService = {
  getCargos: (params = {}) => api.get('/api/v1/institution/cargos/', { params }),
  getCargo: (id) => api.get(`/api/v1/institution/cargos/${id}`),
  createCargo: (data) => api.post('/api/v1/institution/cargos/', data),
  updateCargo: (id, data) => api.put(`/api/v1/institution/cargos/${id}`, data),
  deleteCargo: (id) => api.delete(`/api/v1/institution/cargos/${id}`),

  getOrgaos: (params = {}) => api.get('/api/v1/institution/orgaos/', { params }),
  getOrgao: (id) => api.get(`/api/v1/institution/orgaos/${id}`),
  createOrgao: (data) => api.post('/api/v1/institution/orgaos/', data),
  updateOrgao: (id, data) => api.put(`/api/v1/institution/orgaos/${id}`, data),
  deleteOrgao: (id) => api.delete(`/api/v1/institution/orgaos/${id}`),

  getSetores: (params = {}) => api.get('/api/v1/institution/setores/', { params }),
  getSetor: (id) => api.get(`/api/v1/institution/setores/${id}`),
  createSetor: (data) => api.post('/api/v1/institution/setores/', data),
  updateSetor: (id, data) => api.put(`/api/v1/institution/setores/${id}`, data),
  deleteSetor: (id) => api.delete(`/api/v1/institution/setores/${id}`),
  getSetoresByOrgao: (orgaoId) => api.get(`/api/v1/institution/setores/orgao/${orgaoId}`),

  getServidores: (params = {}) => api.get('/api/v1/institution/servidores/', { params }),
  getServidor: (id) => api.get(`/api/v1/institution/servidores/${id}`),
  createServidor: (data) => api.post('/api/v1/institution/servidores/', data),
  updateServidor: (id, data) => api.put(`/api/v1/institution/servidores/${id}`, data),
  deleteServidor: (id) => api.delete(`/api/v1/institution/servidores/${id}`),
  getServidoresBySetor: (setorId) => api.get(`/api/v1/institution/servidores/setor/${setorId}`),
  getServidoresByPerson: (personId) => api.get(`/api/v1/institution/servidores/person/${personId}`),

  cargoCrud: {
    getList: (params = {}) => api.get('/api/v1/institution/cargos/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/institution/cargos/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/institution/cargos/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/institution/cargos/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/institution/cargos/${id}`),
  },
  orgaoCrud: {
    getList: (params = {}) => api.get('/api/v1/institution/orgaos/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/institution/orgaos/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/institution/orgaos/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/institution/orgaos/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/institution/orgaos/${id}`),
  },
  setorCrud: {
    getList: (params = {}) => api.get('/api/v1/institution/setores/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/institution/setores/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/institution/setores/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/institution/setores/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/institution/setores/${id}`),
  },
  servidorCrud: {
    getList: (params = {}) => api.get('/api/v1/institution/servidores/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/institution/servidores/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/institution/servidores/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/institution/servidores/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/institution/servidores/${id}`),
  },
};

// Serviços para Process Service (Processos, Tramitações, Tipos de mídia)
export const processService = {
  getTiposMidia: (params = {}) => api.get('/api/v1/process/tipos-midia/', { params }),
  getTipoMidia: (id) => api.get(`/api/v1/process/tipos-midia/${id}`),
  createTipoMidia: (data) => api.post('/api/v1/process/tipos-midia/', data),
  updateTipoMidia: (id, data) => api.put(`/api/v1/process/tipos-midia/${id}`, data),
  deleteTipoMidia: (id) => api.delete(`/api/v1/process/tipos-midia/${id}`),

  getProcessos: (params = {}) => api.get('/api/v1/process/processos/', { params }),
  getProcesso: (id) => api.get(`/api/v1/process/processos/${id}`),
  createProcesso: (data) => api.post('/api/v1/process/processos/', data),
  updateProcesso: (id, data) => api.put(`/api/v1/process/processos/${id}`, data),
  deleteProcesso: (id) => api.delete(`/api/v1/process/processos/${id}`),
  getProcessosBySituacao: (situacao) => api.get('/api/v1/process/processos/by-situacao', { params: { situacao } }),
  getProcessosByTipo: (tipo) => api.get('/api/v1/process/processos/by-tipo', { params: { tipo } }),
  getProcessosByOrgao: (orgao_origem_id) => api.get('/api/v1/process/processos/by-orgao', { params: { orgao_origem_id } }),

  getTramitacoesByProcesso: (processoId) => api.get(`/api/v1/process/tramitacoes/by-processo/${processoId}`),
  getTramitacao: (id) => api.get(`/api/v1/process/tramitacoes/${id}`),
  createTramitacao: (data) => api.post('/api/v1/process/tramitacoes/', data),
  updateTramitacao: (id, data) => api.put(`/api/v1/process/tramitacoes/${id}`, data),
  responderExigencia: (id, data) => api.post(`/api/v1/process/tramitacoes/${id}/resposta`, data),
  analisarExigencia: (id, data) => api.post(`/api/v1/process/tramitacoes/${id}/analise`, data),
  deleteTramitacao: (id) => api.delete(`/api/v1/process/tramitacoes/${id}`),
  transitionProcesso: (id, situacao, parecer) =>
    api.post(`/api/v1/process/processos/${id}/transition`, { situacao, parecer: parecer || '' }),
  getInbox: (orgaoId, extra = {}) =>
    api.get('/api/v1/process/inbox', {
      params: {
        ...(orgaoId ? { orgao_id: orgaoId } : {}),
        ...extra,
      },
    }),
  createProtocolo: (data) => api.post('/api/v1/process/protocolos/', data),
  consultarProcesso: (numero) => api.get('/api/v1/process/processos/consulta', { params: { numero } }),
  listTaxas: (params = {}) => api.get('/api/v1/process/taxas/', { params }),
  listTaxasByProcesso: (processoId) => api.get(`/api/v1/process/taxas/by-processo/${processoId}`),
  createTaxa: (data) => api.post('/api/v1/process/taxas/', data),
  updateTaxa: (id, data) => api.patch(`/api/v1/process/taxas/${id}`, data),
  getDocumentoVersions: (documentoId) =>
    api.get(`/api/v1/process/documentos-processo-versionamento/by-documento-processo/${documentoId}`),
  createDocumentoVersion: (data) => api.post('/api/v1/process/documentos-processo-versionamento/', data),
  downloadDocumentoFile: (id) =>
    api.get(`/api/v1/process/documentos-processo/${id}/file`, { responseType: 'blob' }),
  getDocumentosByProcesso: (processoId) => api.get(`/api/v1/process/documentos-processo/by-processo/${processoId}`),
  createDocumentoProcesso: (data) => api.post('/api/v1/process/documentos-processo/', data),
  uploadDocumentoProcesso: (processoId, tipoDocumentoId, file) => {
    const body = new FormData();
    body.append('processo_id', processoId);
    body.append('tipo_documento_id', tipoDocumentoId);
    body.append('file', file);
    return api.post('/api/v1/process/documentos-processo/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocumentoProcesso: (id) => api.delete(`/api/v1/process/documentos-processo/${id}`),
  getDocumentosByTramitacao: (tramitacaoId) => api.get(`/api/v1/process/documentos-tramitacao/by-tramitacao/${tramitacaoId}`),
  createDocumentoTramitacao: (data) => api.post('/api/v1/process/documentos-tramitacao/', data),

  tipoMidiaCrud: {
    getList: (params = {}) => api.get('/api/v1/process/tipos-midia/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/process/tipos-midia/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/process/tipos-midia/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/process/tipos-midia/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/process/tipos-midia/${id}`),
  },
  listPedidos: (params = {}) => api.get('/api/v1/process/pedidos/', { params }).then((r) => r.data),
  createPedido: (formData) =>
    api.post('/api/v1/process/pedidos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  downloadPedidoAnexo: (anexoId) =>
    api.get(`/api/v1/process/pedidos/anexos/${anexoId}/file`, { responseType: 'blob' }),
  listRequerimentos: (params = {}) => api.get('/api/v1/process/requerimentos/', { params }).then((r) => r.data),
  getRequerimento: (id) => api.get(`/api/v1/process/requerimentos/${id}`).then((r) => r.data),
  createRequerimento: (data) => api.post('/api/v1/process/requerimentos/', data).then((r) => r.data),
  updateRequerimento: (id, data) => api.patch(`/api/v1/process/requerimentos/${id}`, data).then((r) => r.data),
  enviarRequerimento: (id) => api.post(`/api/v1/process/requerimentos/${id}/enviar`).then((r) => r.data),
  devolverRequerimento: (id, parecer) =>
    api.post(`/api/v1/process/requerimentos/${id}/devolver`, { parecer }).then((r) => r.data),
  solicitarDesbloqueioRequerimento: (id, parecer) =>
    api.post(`/api/v1/process/requerimentos/${id}/solicitar-desbloqueio`, { parecer }).then((r) => r.data),
  liberarEdicaoRequerimento: (id) =>
    api.post(`/api/v1/process/requerimentos/${id}/liberar-edicao`).then((r) => r.data),
  negarDesbloqueioRequerimento: (id, parecer) =>
    api.post(`/api/v1/process/requerimentos/${id}/negar-desbloqueio`, { parecer }).then((r) => r.data),
  indeferirRequerimento: (id, parecer) =>
    api.post(`/api/v1/process/requerimentos/${id}/indeferir`, { parecer }).then((r) => r.data),
  abrirProcessoRequerimento: (id, data) =>
    api.post(`/api/v1/process/requerimentos/${id}/abrir-processo`, data).then((r) => r.data),
  criarMensagemChecklist: (reqId, itemId, texto) =>
    api.post(`/api/v1/process/requerimentos/${reqId}/checklist/${itemId}/mensagens`, { texto }).then((r) => r.data),
  atualizarMensagemChecklist: (reqId, mensagemId, texto) =>
    api.patch(`/api/v1/process/requerimentos/${reqId}/checklist/mensagens/${mensagemId}`, { texto }).then((r) => r.data),
  uploadRequerimentoAnexo: (reqId, itemId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post(`/api/v1/process/requerimentos/${reqId}/checklist/${itemId}/anexos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  downloadRequerimentoAnexo: (anexoId) =>
    api.get(`/api/v1/process/requerimentos/anexos/${anexoId}/file`, { responseType: 'blob' }),
  deleteRequerimentoAnexo: (anexoId) => api.delete(`/api/v1/process/requerimentos/anexos/${anexoId}`),
  listDespachos: (processoId) =>
    api.get('/api/v1/process/despachos/', { params: { processo_id: processoId } }).then((r) => r.data),
  createDespacho: (data) => api.post('/api/v1/process/despachos/', data).then((r) => r.data),
  updateDespacho: (id, data) => api.patch(`/api/v1/process/despachos/${id}`, data).then((r) => r.data),
  deleteDespacho: (id) => api.delete(`/api/v1/process/despachos/${id}`),
  publishDespacho: (id) => api.post(`/api/v1/process/despachos/${id}/publicar`).then((r) => r.data),
  previewDespachoPdf: (data) =>
    api.post('/api/v1/process/despachos/preview-pdf', data, { responseType: 'blob' }),
  downloadDespachoPdf: (id) =>
    api.get(`/api/v1/process/despachos/${id}/pdf`, { responseType: 'blob' }),
  downloadProcessoPdf: (id) =>
    api.get(`/api/v1/process/processos/${id}/pdf`, { responseType: 'blob' }),

  processoCrud: {
    getList: (params = {}) => api.get('/api/v1/process/processos/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/process/processos/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/process/processos/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/process/processos/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/process/processos/${id}`),
  },
};

// Legislação / assistente de cadastro (corpus federal + LLM opcional)
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pollLegislationJob(jobId, { intervalMs = 2000, timeoutMs = 300000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const { data } = await api.get(`/api/v1/legislation/jobs/${jobId}`);
    if (data.status === 'done') return data.result;
    if (data.status === 'error') {
      const err = new Error('legislation_job_failed');
      err.correlationId = data.correlation_id;
      throw err;
    }
    await sleep(intervalMs);
  }
  const timeoutErr = new Error('legislation_job_timeout');
  timeoutErr.code = 'ECONNABORTED';
  throw timeoutErr;
}

export const legislationService = {
  createJob: (body) =>
    api.post('/api/v1/legislation/jobs', body, { timeout: 30000 }),
  getJob: (jobId) => api.get(`/api/v1/legislation/jobs/${jobId}`),
  pollJob: pollLegislationJob,
  analyzeRegistration: async (data) => {
    const { data: accepted } = await api.post(
      '/api/v1/legislation/jobs',
      {
        type: 'analyze_registration',
        payload: { body: data },
      },
      { timeout: 30000 },
    );
    const correlationId = accepted.correlation_id;
    try {
      const result = await pollLegislationJob(accepted.job_id);
      return { data: result, correlationId, jobId: accepted.job_id };
    } catch (e) {
      e.correlationId = e.correlationId || correlationId;
      throw e;
    }
  },
  search: (params) => api.get('/api/v1/legislation/search', { params }),
  lexmlSearch: (params) => api.get('/api/v1/legislation/lexml-search', { params }),
  status: () => api.get('/api/v1/legislation/status'),
  officialLinks: () => api.get('/api/v1/legislation/official-links'),
  jobs: (params = {}) => api.get('/api/v1/legislation/jobs', { params }),
  gerarParecer: (data) => api.post('/api/v1/legislation/parecer', data, { timeout: 90000 }),
};

// Serviços para License Service (Licenças, Tipos, Condicionantes, etc.)
export const licenseService = {
  getTiposLicenca: (params = {}) => api.get('/api/v1/license/tipos-licenca/', { params }),
  getTipoLicenca: (id) => api.get(`/api/v1/license/tipos-licenca/${id}`),
  createTipoLicenca: (data) => api.post('/api/v1/license/tipos-licenca/', data),
  updateTipoLicenca: (id, data) => api.put(`/api/v1/license/tipos-licenca/${id}`, data),
  deleteTipoLicenca: (id) => api.delete(`/api/v1/license/tipos-licenca/${id}`),

  getLicencas: (params = {}) => api.get('/api/v1/license/licencas/', { params }),
  getLicenca: (id) => api.get(`/api/v1/license/licencas/${id}`),
  createLicenca: (data) => api.post('/api/v1/license/licencas/', data),
  vincularArquivoLicenca: (licencaId, arquivoId) =>
    api.post(`/api/v1/license/licencas/${licencaId}/vincular-arquivo/${arquivoId}`),
  updateLicenca: (id, data) => api.put(`/api/v1/license/licencas/${id}`, data),
  deleteLicenca: (id) => api.delete(`/api/v1/license/licencas/${id}`),
  getLicencaByProcesso: (processoId) => api.get(`/api/v1/license/licencas/by-processo/${processoId}`),
  getLicencasByStatus: (status) => api.get('/api/v1/license/licencas/by-status', { params: { status } }),
  listAssinaturas: (licencaId) => api.get(`/api/v1/license/licencas/${licencaId}/assinaturas`),
  assinarLicenca: (licencaId, data) => api.post(`/api/v1/license/licencas/${licencaId}/assinar`, data),
  transitionLicenca: (id, status, parecer) =>
    api.post(`/api/v1/license/licencas/${id}/transition`, { status, parecer: parecer || '' }),
  getEventos: (params = {}) => api.get('/api/v1/license/eventos/', { params }),
  createEvento: (data) => api.post('/api/v1/license/eventos/', data),
  updateEvento: (id, data) => api.put(`/api/v1/license/eventos/${id}`, data),
  deleteEvento: (id) => api.delete(`/api/v1/license/eventos/${id}`),
  listHorariosEvento: (eventoId) => api.get(`/api/v1/license/horarios-evento/by-evento/${eventoId}`),
  createHorarioEvento: (data) => api.post('/api/v1/license/horarios-evento/', data),
  deleteHorarioEvento: (id) => api.delete(`/api/v1/license/horarios-evento/${id}`),
  listTiposEvento: () => api.get('/api/v1/license/tipos-evento/'),
  createTipoEvento: (data) => api.post('/api/v1/license/tipos-evento/', data),
  listTiposCertidao: () => api.get('/api/v1/license/tipos-certidao/'),
  createTipoCertidao: (data) => api.post('/api/v1/license/tipos-certidao/', data),
  getSolicitacoes: (params = {}) => api.get('/api/v1/license/solicitacoes/', { params }),
  createSolicitacao: (data) => api.post('/api/v1/license/solicitacoes/', data),
  updateSolicitacao: (id, data) => api.put(`/api/v1/license/solicitacoes/${id}`, data),
  deleteSolicitacao: (id) => api.delete(`/api/v1/license/solicitacoes/${id}`),
  getCertidoes: (params = {}) => api.get('/api/v1/license/certidoes-viabilidade/', { params }),
  consultarCertidao: (numero) => api.get('/api/v1/license/certidoes-viabilidade/consulta', { params: { numero } }),
  consultarLicencasPublicas: (params = {}) => api.get('/api/v1/license/licencas/consulta', { params }),
  createCertidao: (data) => api.post('/api/v1/license/certidoes-viabilidade/', data),
  updateCertidao: (id, data) => api.put(`/api/v1/license/certidoes-viabilidade/${id}`, data),
  deleteCertidao: (id) => api.delete(`/api/v1/license/certidoes-viabilidade/${id}`),
  getDocumentosLicenca: (licencaId) => api.get(`/api/v1/license/documentos-licenca/by-licenca/${licencaId}`),
  createDocumentoLicenca: (data) => api.post('/api/v1/license/documentos-licenca/', data),
  uploadDocumentoLicenca: (licencaId, tipoDocumentoId, file, informacao) => {
    const body = new FormData();
    body.append('licenca_id', licencaId);
    body.append('tipo_documento_id', tipoDocumentoId);
    body.append('file', file);
    if (informacao) body.append('informacao', informacao);
    return api.post('/api/v1/license/documentos-licenca/upload', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadDocumentoLicenca: (id) =>
    api.get(`/api/v1/license/documentos-licenca/${id}/file`, { responseType: 'blob' }),
  updateDocumentoLicenca: (id, data) => api.put(`/api/v1/license/documentos-licenca/${id}`, data),
  deleteDocumentoLicenca: (id) => api.delete(`/api/v1/license/documentos-licenca/${id}`),
  generatePlanoRegularizacao: (payload = {}) =>
    api.post('/api/v1/license/planos-regularizacao/generate', payload),
  listPlanosRegularizacao: () => api.get('/api/v1/license/planos-regularizacao/'),
  getPlanoRegularizacao: (id) => api.get(`/api/v1/license/planos-regularizacao/${id}`),
  updatePlanoItem: (planId, itemId, data) =>
    api.patch(`/api/v1/license/planos-regularizacao/${planId}/itens/${itemId}`, data),
  /** Extrai o PDF, guarda o arquivo e completa a licença se o número já existir. */
  importLicencaFromPdf: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/v1/license/licencas/importar-pdf', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { use_llm: true },
      timeout: 90000,
    });
  },
  getCondicionantesByLicenca: (licencaId) =>
    api.get(`/api/v1/license/condicionantes/by-licenca/${licencaId}`),
  searchCondicionantes: (params = {}) =>
    api.get('/api/v1/license/condicionantes/search', { params }),
  getCondicionante: (id) => api.get(`/api/v1/license/condicionantes/${id}`),
  createCondicionante: (data) => api.post('/api/v1/license/condicionantes/', data),
  updateCondicionante: (id, data) => api.put(`/api/v1/license/condicionantes/${id}`, data),
  deleteCondicionante: (id) => api.delete(`/api/v1/license/condicionantes/${id}`),

  listCondicionanteAnexos: (condicionanteId) =>
    api.get(`/api/v1/license/condicionante-anexos/by-condicionante/${condicionanteId}`),
  uploadCondicionanteAnexo: (condicionanteId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/v1/license/condicionante-anexos/upload/${condicionanteId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCondicionanteAnexo: (anexoId) => api.delete(`/api/v1/license/condicionante-anexos/${anexoId}`),
  getCondicionanteAnexoBlob: (anexoId) =>
    api.get(`/api/v1/license/condicionante-anexos/${anexoId}/serve`, { responseType: 'blob' }),

  listTipoChecklist: (tipoId) => api.get(`/api/v1/license/tipos-licenca/${tipoId}/checklist`),
  createTipoChecklistItem: (tipoId, data) =>
    api.post(`/api/v1/license/tipos-licenca/${tipoId}/checklist`, data),
  updateTipoChecklistItem: (itemId, data) =>
    api.put(`/api/v1/license/tipos-licenca/checklist/${itemId}`, data),
  deleteTipoChecklistItem: (itemId) => api.delete(`/api/v1/license/tipos-licenca/checklist/${itemId}`),
  listLicencaChecklist: (licencaId) => api.get(`/api/v1/license/licencas/${licencaId}/checklist`),
  createLicencaChecklistItem: (licencaId, data) =>
    api.post(`/api/v1/license/licencas/${licencaId}/checklist`, data),
  updateLicencaChecklistItem: (itemId, data) =>
    api.put(`/api/v1/license/licencas/checklist/${itemId}`, data),
  deleteLicencaChecklistItem: (itemId) => api.delete(`/api/v1/license/licencas/checklist/${itemId}`),
  listLicencaChecklistAnexos: (itemId) =>
    api.get(`/api/v1/license/licencas/checklist/${itemId}/anexos`),
  uploadLicencaChecklistAnexo: (itemId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/v1/license/licencas/checklist/${itemId}/anexos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLicencaChecklistAnexo: (anexoId) =>
    api.delete(`/api/v1/license/licencas/checklist/anexos/${anexoId}`),
  getLicencaChecklistAnexoBlob: (anexoId) =>
    api.get(`/api/v1/license/licencas/checklist/anexos/${anexoId}/serve`, { responseType: 'blob' }),
  patchPlanoItem: (planId, itemId, status) =>
    api.patch(`/api/v1/license/planos-regularizacao/${planId}/itens/${itemId}`, { status }),

  tipoLicencaCrud: {
    getList: (params = {}) => api.get('/api/v1/license/tipos-licenca/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/license/tipos-licenca/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/license/tipos-licenca/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/license/tipos-licenca/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/license/tipos-licenca/${id}`),
  },
  licencaCrud: {
    getList: (params = {}) => api.get('/api/v1/license/licencas/', { params }).then((r) => r.data),
    getItem: (id) => api.get(`/api/v1/license/licencas/${id}`).then((r) => r.data),
    create: (data) => api.post('/api/v1/license/licencas/', data).then((r) => r.data),
    update: (id, data) => api.put(`/api/v1/license/licencas/${id}`, data).then((r) => r.data),
    delete: (id) => api.delete(`/api/v1/license/licencas/${id}`),
  },

  /** Anexos PDF da licença (concatenação no servidor) */
  listLicencaAnexosPdf: (licencaId) =>
    api.get(`/api/v1/license/licenca-anexos/by-licenca/${licencaId}`),
  uploadLicencaAnexoPdf: (licencaId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/api/v1/license/licenca-anexos/upload/${licencaId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLicencaAnexoPdf: (anexoId) => api.delete(`/api/v1/license/licenca-anexos/${anexoId}`),
  getLicencaAnexosPdfMergedBlob: (licencaId) =>
    api.get(`/api/v1/license/licenca-anexos/by-licenca/${licencaId}/pdf-merged`, {
      responseType: 'blob',
    }),
};

// Serviços para Dashboard e Relatórios
export const dashboardService = {
  getStats: (period = 'month') => api.get('/api/v1/dashboard/stats', { params: { period } }),
  getLicensesByStatus: () => api.get('/api/v1/dashboard/licenses-by-status'),
  getLicensesTrend: (period = 'month') =>
    api.get('/api/v1/dashboard/licenses-trend', { params: { period } }),
  getInsights: (period = 'month') => api.get('/api/v1/dashboard/insights', { params: { period } }),
  getRecentActivity: (limit = 10) => api.get(`/api/v1/dashboard/recent-activity?limit=${limit}`),
  getMapData: () => api.get('/api/v1/dashboard/map-data'),
  getGeoJson: (params = {}) => api.get('/api/v1/dashboard/geojson', { params }),
  getGeoOverlap: (params) => api.get('/api/v1/dashboard/geo/overlap', { params }),
  getProtectedIntersect: (enterpriseId) =>
    api.get('/api/v1/dashboard/geo/protected-intersect', { params: { enterprise_id: enterpriseId } }),
};

export const meService = {
  getTasks: () => api.get('/api/v1/me/tasks'),
  getServidor: () => api.get('/api/v1/me/servidor'),
};

/** Central in-app + status dos canais (e-mail, WhatsApp, SMS). */
export const notificationService = {
  getStatus: () => api.get('/api/v1/notifications/status'),
  listInApp: (params = {}) => api.get('/api/v1/notifications/in-app', { params }),
  getInAppUnreadCount: (params = {}) =>
    api.get('/api/v1/notifications/in-app/unread-count', { params }),
  markInAppRead: (notificationId) =>
    api.patch(`/api/v1/notifications/in-app/${notificationId}/read`, null),
  markAllInAppRead: () => api.post('/api/v1/notifications/in-app/read-all', {}),

  listTypes: () => api.get('/api/v1/notifications/types'),
  createType: (data) => api.post('/api/v1/notifications/types', data),
  updateType: (id, data) => api.put(`/api/v1/notifications/types/${id}`, data),
  deleteType: (id) => api.delete(`/api/v1/notifications/types/${id}`),

  listTemplates: (params = {}) => api.get('/api/v1/notifications/templates', { params }),
  getTemplate: (id) => api.get(`/api/v1/notifications/templates/${id}`),
  createTemplate: (data) => api.post('/api/v1/notifications/templates', data),
  updateTemplate: (id, data) => api.put(`/api/v1/notifications/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/api/v1/notifications/templates/${id}`),
  previewTemplate: (data) => api.post('/api/v1/notifications/templates/preview', data),

  send: (data) => api.post('/api/v1/notifications/send', data),
  getRules: () => api.get('/api/v1/notifications/rules'),
  updateRules: (data) => api.put('/api/v1/notifications/rules', data),
};

// Serviços para Autenticação
export const organizationService = {
  getPublicChannels: () => api.get('/api/v1/organizations/me/settings'),
  updatePublicChannels: (data) => api.patch('/api/v1/organizations/me/settings', data),
  uploadBranding: (kind, file) => {
    const body = new FormData();
    body.append('file', file);
    return api.post(`/api/v1/organizations/me/branding/${kind}`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBranding: (kind) => api.delete(`/api/v1/organizations/me/branding/${kind}`),
};

export const authService = {
  login: (credentials) => api.post('/api/v1/auth/login', credentials),
  login2fa: (tempToken, code) => api.post('/api/v1/auth/login/2fa', { temp_token: tempToken, code }),
  register: (userData) => api.post('/api/v1/auth/register', userData),
  logout: () => api.post('/api/v1/auth/logout'),
  refreshToken: () => {
    const refresh_token = localStorage.getItem('refreshToken');
    return api.post('/api/v1/auth/refresh', { refresh_token });
  },
  getProfile: () => api.get('/api/v1/auth/me'),
  updateProfile: (data) => api.put('/api/v1/auth/profile', data),
  requestPasswordReset: (data) => api.post('/api/v1/auth/password-reset/request', data),
  confirmPasswordReset: (data) => api.post('/api/v1/auth/password-reset/confirm', data),
  listUsers: (params = {}) => api.get('/api/v1/auth/users', { params }),
  resendActivation: (userId) => api.post(`/api/v1/auth/users/${userId}/resend-activation`),
  assignUserRole: (userId, roleName) => api.post(`/api/v1/auth/users/${userId}/roles`, { role_name: roleName }),
  removeUserRole: (userId, roleName) => api.delete(`/api/v1/auth/users/${userId}/roles/${roleName}`),
  listUserRoleAudit: (userId) => api.get(`/api/v1/auth/users/${userId}/role-audit`),
  getUserRoleAudit: (userId) => api.get(`/api/v1/auth/users/${userId}/role-audit`),
  updateUserEntity: (userId, data) => api.put(`/api/v1/auth/users/${userId}/entity`, data),
  enable2fa: () => api.post('/api/v1/auth/2fa/enable'),
  confirm2fa: (code) => api.post('/api/v1/auth/2fa/confirm', { code }),
  disable2fa: (code) => api.post('/api/v1/auth/2fa/disable', { code }),
};

export const observabilityService = {
  listEvents: (params = {}) => api.get('/api/v1/observability/events', { params }),
  getJob: (id) => api.get(`/api/v1/observability/jobs/${id}`),
  getTimeline: (id) => api.get(`/api/v1/observability/jobs/${id}/timeline`),
  health: () => api.get('/api/v1/observability/health'),
};

export const auditService = {
  catalog: () => api.get('/api/v1/audit/catalog'),
  history: (params = {}) => api.get('/api/v1/audit/history', { params }),
  record: (entityType, entityId, params = {}) =>
    api.get(`/api/v1/audit/records/${entityType}/${entityId}`, { params }),
  trash: (params = {}) => api.get('/api/v1/audit/trash', { params }),
  restore: (entityType, entityId) => api.post(`/api/v1/audit/restore/${entityType}/${entityId}`),
};

export const fiscalizacaoService = {
  listVistorias: (params = {}) => api.get('/api/v1/complaint/vistorias/', { params }),
  createVistoria: (data) => api.post('/api/v1/complaint/vistorias/', data),
  updateVistoria: (id, data) => api.patch(`/api/v1/complaint/vistorias/${id}`, data),
  downloadVistoriaFoto: (id) =>
    api.get(`/api/v1/complaint/vistorias/${id}/foto`, { responseType: 'blob' }),
  listAutos: (params = {}) => api.get('/api/v1/complaint/autos/', { params }),
  createAuto: (data) => api.post('/api/v1/complaint/autos/', data),
  updateAuto: (id, data) => api.patch(`/api/v1/complaint/autos/${id}`, data),
  downloadAutoPdf: (id) => api.get(`/api/v1/complaint/autos/${id}/pdf`, { responseType: 'blob' }),
};

export const reportsApi = {
  catalog: (params = {}) => api.get('/api/v1/reports/catalog', { params }),
  generate: (code, params = {}) => api.get(`/api/v1/reports/${code}`, { params, responseType: 'blob' }),
};

async function readAssistantSse(response, { onToken, onStatus } = {}) {
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const detail = payload?.detail;
    throw new Error(i18n.t('error', { ns: 'assistant' }));
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error(i18n.t('error', { ns: 'assistant' }));
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((item) => item.startsWith('data: '));
      if (!line) continue;
      let event;
      try {
        event = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (event.type === 'token' && event.text) onToken?.(event.text);
      else if (event.type === 'status') onStatus?.(event.text);
      else if (event.type === 'done') donePayload = event;
      else if (event.type === 'error') throw new Error(i18n.t('error', { ns: 'assistant' }));
    }
  }
  if (!donePayload) throw new Error(i18n.t('error', { ns: 'assistant' }));
  return donePayload;
}

export const assistantService = {
  config: () => api.get('/api/v1/assistant/config'),
  insight: () => api.get('/api/v1/assistant/insight'),
  query: (message, { confirmToken, sessionId, guidedTool, draftArgs } = {}) => {
    const body = { message };
    if (confirmToken) body.confirm_token = confirmToken;
    if (sessionId) body.session_id = sessionId;
    if (guidedTool) body.guided_tool = guidedTool;
    if (draftArgs) body.draft_args = draftArgs;
    return api.post('/api/v1/assistant/query', body, { timeout: 90000 });
  },
  stream: async (message, { confirmToken, sessionId, guidedTool, draftArgs, onToken, onStatus } = {}) => {
    const body = { message };
    if (confirmToken) body.confirm_token = confirmToken;
    if (sessionId) body.session_id = sessionId;
    if (guidedTool) body.guided_tool = guidedTool;
    if (draftArgs) body.draft_args = draftArgs;
    const response = await fetch(apiPath('/api/v1/assistant/query/stream'), {
      method: 'POST',
      headers: getApiAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return readAssistantSse(response, { onToken, onStatus });
  },
};

// Serviços para Upload de Arquivos
export const fileService = {
  list: () => api.get('/api/v1/license/arquivos/'),
  upload: (file, { description, tags } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    if (tags) formData.append('tags', tags);
    return api.post('/api/v1/license/arquivos/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (fileId) => api.delete(`/api/v1/license/arquivos/${fileId}`),
  deleteBySource: (source, fileId) => {
    if (source === 'condicionante') return licenseService.deleteCondicionanteAnexo(fileId);
    if (source === 'checklist') return licenseService.deleteLicencaChecklistAnexo(fileId);
    if (source === 'licenca_pdf') return licenseService.deleteLicencaAnexoPdf(fileId);
    return api.delete(`/api/v1/license/arquivos/${fileId}`);
  },
  getFile: (fileId) => api.get(`/api/v1/license/arquivos/${fileId}`),
  getBlob: (source, fileId) =>
    api.get(`/api/v1/license/arquivos/${source}/${fileId}/serve`, { responseType: 'blob' }),
};

// Utilitários para tratamento de erros
export const handleApiError = (error) => {
  return {
    message: userFacingError(error),
    status: error?.response?.status || 0,
    details: null,
  };
};

export default api;
